import {
  Component,
  Inject,
  Renderer2,
  ViewEncapsulation,
  OnDestroy,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TranslationService } from '@alfresco/adf-core';
import { WKGenericPropertyUpdateService } from '../../services';
import {
  getCurrentDocumentId,
  SnackbarErrorAction,
  SnackbarInfoAction,
} from '../../store';
import { loadDocumentAndContent } from '../../modules/wk-document-decorator';

@Component({
  selector: 'wk-update-property-dialog',
  templateUrl: './wk-update-property.dialog.html',
  styleUrls: ['./wk-update-property.dialog.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WkUpdatePropertyDialogComponent implements OnDestroy {
  propertyName = '';
  propertyValue = '';
  showContent = false;
  private nodeId: string;
  private readonly onDestroy$ = new Subject<void>();
  private unlistener: () => void;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialogRef: MatDialogRef<WkUpdatePropertyDialogComponent>,
    private readonly renderer: Renderer2,
    private readonly genericPropertyUpdateService: WKGenericPropertyUpdateService,
    private readonly store: Store,
    private readonly translationService: TranslationService
  ) {
    this.dialogRef.afterOpened().subscribe(() => {
      this.showContent = true;
    });

    this.store
      .select(getCurrentDocumentId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((id: string) => (this.nodeId = id));
  }

  /**
   * Listens for overlay backdrop clicks and closes the dialog when detected.
   */
  ngOnInit(): void {
    this.unlistener = this.renderer.listen('document', 'click', (event) => {
      if (event.target.hasAttribute('hasOverlayClickClose')) {
        this.closeDialog();
      }
    });
  }

  /**
   * Resets the property name and value input fields and closes the dialog.
   */
  closeDialog(): void {
    this.propertyName = '';
    this.propertyValue = '';
    this.dialogRef.close();
  }

  /**
   * Calls the generic property update API with the entered property name and value.
   * Dispatches a success snackbar and closes the dialog on success,
   * or dispatches an error snackbar on failure.
   * No-ops if either field is empty.
   */
  updateProperty(): void {
    if (!this.propertyName || !this.propertyValue) {
      return;
    }

    this.genericPropertyUpdateService
      .updateProperty(this.nodeId, this.propertyName, this.propertyValue)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.store.dispatch(
            new SnackbarInfoAction(
              this.translationService.instant(
                'APP.DIALOGS.UPDATE_PROPERTY_DIALOG.UPDATE_SUCCESS'
              )
            )
          );
          this.closeDialog();
          this.store.dispatch(
            loadDocumentAndContent({ id: this.nodeId, url: '' })
          );
        },
        error: () => {
          this.store.dispatch(
            new SnackbarErrorAction(
              this.translationService.instant(
                'APP.DIALOGS.UPDATE_PROPERTY_DIALOG.UPDATE_ERROR'
              )
            )
          );
        },
      });
  }

  /**
   * Removes the document click listener and completes active subscriptions.
   */
  ngOnDestroy(): void {
    this.unlistener?.();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
